/**
 * Hybrid local + server preferences for the Ambient Scene launcher.
 *
 *  - Anonymous users: localStorage only.
 *  - Logged-in users: read-through from the server on launcher mount,
 *    then mirror every write to both localStorage AND `PUT /api/ambient-sounds/prefs`.
 *
 * State shape (one document per user):
 *  {
 *    last:      { kind: 'curated'|'library', id?, label?, sound?, at } | null
 *    favorites: [{id, name, username, license, duration, preview_url, freesound_url, added_at}]
 *    mixes:     [{mix_id, name, curated_id?, curated_label?, library_sound?, created_at}]
 *  }
 *
 * All reads/writes are guarded against SSR + quota errors.
 */

const LAST_KEY = 'lumera.ambient.last';
const FAV_KEY = 'lumera.ambient.favorites';
const MIX_KEY = 'lumera.ambient.mixes';
const FAV_MAX = 30;
const MIX_MAX = 50;

const API = process.env.REACT_APP_BACKEND_URL;

const safeParse = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (_) { return null; }
};

const safeWrite = (key, value) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(key, JSON.stringify(value)); }
  catch (_) { /* quota or privacy-mode — ignore */ }
};

const safeRemove = (key) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(key); } catch (_) { /* ignore */ }
};

/* ------------------------- LOCAL accessors ------------------------- */
export const getLastAmbient = () => {
  if (typeof window === 'undefined') return null;
  const parsed = safeParse(window.localStorage.getItem(LAST_KEY));
  if (!parsed || typeof parsed !== 'object') return null;
  if (parsed.kind === 'curated' && typeof parsed.id === 'string') return parsed;
  if (parsed.kind === 'library' && parsed.sound?.id) return parsed;
  return null;
};

export const getFavorites = () => {
  if (typeof window === 'undefined') return [];
  const parsed = safeParse(window.localStorage.getItem(FAV_KEY));
  return Array.isArray(parsed) ? parsed.filter((s) => s?.id) : [];
};

export const getMixes = () => {
  if (typeof window === 'undefined') return [];
  const parsed = safeParse(window.localStorage.getItem(MIX_KEY));
  return Array.isArray(parsed) ? parsed.filter((m) => m?.mix_id && m?.name) : [];
};

export const isFavorite = (soundId) =>
  getFavorites().some((s) => s.id === soundId);

/* ------------------------- LOCAL writers ------------------------- */
const writeLast = (entry) => safeWrite(LAST_KEY, entry);
const writeFavorites = (list) => safeWrite(FAV_KEY, list);
const writeMixes = (list) => safeWrite(MIX_KEY, list);

export const setLastCurated = (sceneId, label) => {
  if (!sceneId) return;
  writeLast({ kind: 'curated', id: sceneId, label, at: Date.now() });
};

export const setLastLibrary = (sound) => {
  if (!sound?.id) return;
  writeLast({
    kind: 'library',
    sound: {
      id: sound.id,
      name: sound.name,
      username: sound.username,
      license: sound.license,
      duration: sound.duration,
      preview_url: sound.preview_url,
      freesound_url: sound.freesound_url,
    },
    at: Date.now(),
  });
};

export const clearLastAmbient = () => safeRemove(LAST_KEY);

export const toggleFavorite = (sound) => {
  if (!sound?.id) return getFavorites();
  const current = getFavorites();
  const existing = current.findIndex((s) => s.id === sound.id);
  let next;
  if (existing >= 0) {
    next = [...current.slice(0, existing), ...current.slice(existing + 1)];
  } else {
    const mini = {
      id: sound.id,
      name: sound.name,
      username: sound.username,
      license: sound.license,
      duration: sound.duration,
      preview_url: sound.preview_url,
      freesound_url: sound.freesound_url,
      added_at: Date.now(),
    };
    next = [mini, ...current].slice(0, FAV_MAX);
  }
  writeFavorites(next);
  return next;
};

export const saveMix = (mix) => {
  if (!mix?.name) return getMixes();
  const current = getMixes();
  const mix_id = mix.mix_id
    || (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`);
  const next = [
    {
      mix_id,
      name: mix.name.slice(0, 60),
      curated_id: mix.curated_id || null,
      curated_label: mix.curated_label || null,
      curated_volume: typeof mix.curated_volume === 'number' ? mix.curated_volume : null,
      library_sound: mix.library_sound || null,
      library_volume: typeof mix.library_volume === 'number' ? mix.library_volume : null,
      created_at: Date.now(),
    },
    ...current.filter((m) => m.mix_id !== mix_id),
  ].slice(0, MIX_MAX);
  writeMixes(next);
  return next;
};

export const deleteMix = (mix_id) => {
  const next = getMixes().filter((m) => m.mix_id !== mix_id);
  writeMixes(next);
  return next;
};

/* ------------------------- REMOTE sync ------------------------- */

/** Fetch server-side prefs and merge into localStorage when authenticated. */
export const fetchRemotePrefs = async () => {
  if (typeof window === 'undefined' || !API) return null;
  try {
    const res = await fetch(`${API}/api/ambient-sounds/prefs`, {
      credentials: 'include',
    });
    if (res.status === 401) return null;          // anonymous
    if (!res.ok) return null;
    const data = await res.json();
    // Server is the source of truth on initial mount — overwrite local copy.
    if (data.last !== undefined) {
      if (data.last) writeLast(data.last);
      else safeRemove(LAST_KEY);
    }
    if (Array.isArray(data.favorites)) writeFavorites(data.favorites);
    if (Array.isArray(data.mixes)) writeMixes(data.mixes);
    return data;
  } catch (_) {
    return null;
  }
};

/** Push the current localStorage snapshot to the server (best-effort). */
let _saveTimer = null;
export const scheduleRemoteSave = () => {
  if (typeof window === 'undefined' || !API) return;
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(async () => {
    _saveTimer = null;
    try {
      await fetch(`${API}/api/ambient-sounds/prefs`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          last: getLastAmbient(),
          favorites: getFavorites(),
          mixes: getMixes(),
        }),
      });
    } catch (_) { /* offline / 401 — keep local copy */ }
  }, 600); // debounce burst writes
};
